import { ArrowForward } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import NextLink from 'next/link';
import { useLastViewed } from '@/views/Home/hooks';
import {
  getAnalyticsRouteNameFromPath,
  getInteractionIdFromPath,
} from '@/views/Home/hooks/useLastViewed/helpers';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';

const CUSTOM_TABLET_BREAKPOINT = 620;

const Container = styled(Stack)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  maxWidth: '100%',
  paddingRight: theme.spacing(3),
  paddingLeft: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  [theme.breakpoints.up(CUSTOM_TABLET_BREAKPOINT)]: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(4),
    paddingTop: theme.spacing(4),
    gap: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(1),
    maxWidth: '50%',
    alignItems: 'flex-start',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '40%',
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  whiteSpace: 'nowrap',
  display: 'flex',
  justifyContent: 'flex-start',
  [theme.breakpoints.up(CUSTOM_TABLET_BREAKPOINT)]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
  },
}));

export const LastViewed = () => {
  const { lastViewed, loading } = useLastViewed();
  const trackEvent = useTrackHomePageAnalytics();

  if (loading)
    return (
      <Container
        sx={{ width: '100%', height: '154px' }}
        data-testid="last-viewed-skeleton"
      >
        <Skeleton width="50%" />
        <Skeleton width="80%" />
        <Skeleton width="100%" />
        <Skeleton width="40%" />
      </Container>
    );

  if (!lastViewed) return null;

  const {
    title,
    description,
    dateTime,
    href,
    isInternalLink,
    fullRoute,
    shouldOpenInNewTab,
  } = lastViewed;

  return (
    <Container data-testid="txm-home-puwylo-section">
      <Stack
        sx={{
          gap: 1,
          px: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {dateTime}
        </Typography>
        <Typography variant="h5" component="p">
          {title}
        </Typography>
        <Divider
          sx={{
            borderBottomWidth: 2,
            borderColor: 'primary.main',
            width: 24,
          }}
        />
        <Typography variant="body3">{description}</Typography>
      </Stack>
      <ButtonContainer>
        <Button
          id="pick-up-where-you-left-off"
          href={href}
          endIcon={<ArrowForward />}
          LinkComponent={isInternalLink ? NextLink : undefined}
          onClick={() => {
            trackEvent(TrackHomePageEvent.PickUpWhereYouLeftOffClick, {
              interactionId: getInteractionIdFromPath(fullRoute),
              routeName: getAnalyticsRouteNameFromPath(fullRoute),
            });
          }}
          target={shouldOpenInNewTab ? '_blank' : undefined}
          rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
        >
          Pick Up Where You Left Off
        </Button>
      </ButtonContainer>
    </Container>
  );
};
